import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import {
  parseEmails,
  parseSocialPosts,
  parseLandingPage,
  extractFullScript,
} from '../lib/contentParsers';
import type { ParsedEmail, ParsedSocialPost, LandingPageContent } from '../types/results';

interface SharedWebinarData {
  id: string;
  title: string | null;
  subtitle: string | null;
  script: string | null;
  marketingEmails: string | null;
  followUpEmails: string | null;
  socialPosts: string | null;
  landingPage: string | null;
}

type LoadState = 'loading' | 'success' | 'error';
type TabType = 'script' | 'emails' | 'social' | 'landing';

const TABS: { key: TabType; label: string }[] = [
  { key: 'script', label: 'Script' },
  { key: 'emails', label: 'Emails' },
  { key: 'social', label: 'Social Posts' },
  { key: 'landing', label: 'Landing Page' },
];

export function SharedWebinar() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<SharedWebinarData | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [activeTab, setActiveTab] = useState<TabType>('script');

  useEffect(() => {
    if (!slug) {
      setState('error');
      return;
    }

    const fetchWebinar = async () => {
      try {
        const res = await fetch(`/api/webinar/${encodeURIComponent(slug)}`);
        if (!res.ok) {
          setState('error');
          return;
        }
        const json = await res.json();
        setData(json);
        setState('success');
      } catch {
        setState('error');
      }
    };

    fetchWebinar();
  }, [slug]);

  // Parse content
  const parsedEmails: ParsedEmail[] = data?.marketingEmails
    ? parseEmails(data.marketingEmails)
    : [];
  const parsedFollowUp: ParsedEmail[] = data?.followUpEmails
    ? parseEmails(data.followUpEmails)
    : [];
  const parsedSocial: ParsedSocialPost[] = data?.socialPosts
    ? parseSocialPosts(data.socialPosts)
    : [];
  const parsedLanding: LandingPageContent | null = data?.landingPage
    ? parseLandingPage(data.landingPage)
    : null;
  const scriptMarkdown: string = data?.script ? extractFullScript(data.script) : '';

  // Loading state
  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Error state
  if (state === 'error' || !data) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-[#020617] to-[#020617]" />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-slate-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            This webinar is not available
          </h2>
          <p className="text-slate-400">
            The webinar you're looking for may have been removed or is no longer
            publicly shared.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Background gradient */}
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-[#020617] to-[#020617]" />

      {/* Accent line */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

      <div className="max-w-4xl mx-auto px-4 py-12 md:py-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          {data.title && (
            <h1
              className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4"
              style={{ textShadow: '0 0 40px rgba(37,99,235,0.3)' }}
            >
              {data.title}
            </h1>
          )}
          {data.subtitle && (
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
              {data.subtitle}
            </p>
          )}
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-1 p-1 bg-slate-900/80 border border-slate-700/50 rounded-xl backdrop-blur-sm">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-blue-600/30 text-white border border-blue-500/30 shadow-lg shadow-blue-500/10'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Script Tab */}
            {activeTab === 'script' && (
              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 md:p-10">
                <div className="prose prose-invert prose-lg max-w-none leading-relaxed prose-headings:text-slate-100 prose-headings:font-semibold prose-p:text-slate-300 prose-strong:text-white prose-li:text-slate-300 prose-hr:border-slate-700/50">
                  <ReactMarkdown>{scriptMarkdown}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Emails Tab */}
            {activeTab === 'emails' && (
              <div className="space-y-6">
                {/* Marketing Emails */}
                {parsedEmails.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold text-slate-200 mb-4">
                      Marketing Emails
                    </h2>
                    <div className="space-y-4">
                      {parsedEmails.map((email) => (
                        <EmailDisplayCard
                          key={`m-${email.number}`}
                          email={email}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Follow-Up Emails */}
                {parsedFollowUp.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold text-slate-200 mb-4">
                      Follow-Up Emails
                    </h2>
                    <div className="space-y-4">
                      {parsedFollowUp.map((email) => (
                        <EmailDisplayCard
                          key={`f-${email.number}`}
                          email={email}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {parsedEmails.length === 0 && parsedFollowUp.length === 0 && (
                  <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center">
                    <p className="text-slate-400">No emails available</p>
                  </div>
                )}
              </div>
            )}

            {/* Social Posts Tab */}
            {activeTab === 'social' && (
              <div className="space-y-4">
                {parsedSocial.length > 0 ? (
                  parsedSocial.map((post) => (
                    <SocialDisplayCard
                      key={post.number}
                      post={post}
                    />
                  ))
                ) : (
                  <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center">
                    <p className="text-slate-400">No social posts available</p>
                  </div>
                )}
              </div>
            )}

            {/* Landing Page Tab */}
            {activeTab === 'landing' && (
              <div className="space-y-4">
                {parsedLanding ? (
                  <LandingPageDisplay content={parsedLanding} />
                ) : (
                  <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-12 text-center">
                    <p className="text-slate-400">
                      No landing page content available
                    </p>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16 space-y-2"
        >
          <p className="text-sm text-slate-500">
            Powered by{' '}
            <span className="text-slate-400 font-medium">Trained Advisor</span>
          </p>
          <p className="text-xs text-slate-600">
            Created with Webinar Wizard
          </p>
        </motion.div>
      </div>
    </div>
  );
}

/* ========================================================================
   Lightweight display components (no auth dependencies)
   ======================================================================== */

function EmailDisplayCard({ email }: { email: ParsedEmail }) {
  return (
    <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 transition-colors hover:border-slate-600/50">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-blue-400">
            {email.number}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-100 truncate">
            {email.subject || `Email ${email.number}`}
          </h3>
          {email.sendDay && (
            <p className="text-xs text-slate-500">{email.sendDay}</p>
          )}
        </div>
      </div>
      <div className="prose prose-invert prose-sm max-w-none prose-p:text-slate-300 prose-p:leading-relaxed">
        <ReactMarkdown>{email.body}</ReactMarkdown>
      </div>
    </div>
  );
}

function SocialDisplayCard({ post }: { post: ParsedSocialPost }) {
  return (
    <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 transition-colors hover:border-slate-600/50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-purple-400">
              {post.number}
            </span>
          </div>
          {post.dayLabel && (
            <span className="text-sm text-slate-400">{post.dayLabel}</span>
          )}
        </div>
        <span className="text-xs text-slate-500 tabular-nums">
          {post.characterCount} chars
        </span>
      </div>
      <div className="prose prose-invert prose-sm max-w-none prose-p:text-slate-300 prose-p:leading-relaxed whitespace-pre-wrap">
        {post.content}
      </div>
    </div>
  );
}

function LandingPageDisplay({ content }: { content: LandingPageContent }) {
  return (
    <div className="space-y-4">
      {/* Title & Description */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
        {content.title && (
          <h3 className="text-xl font-bold text-slate-100 mb-2">
            {content.title}
          </h3>
        )}
        {content.subtitle && (
          <p className="text-slate-400 mb-4">{content.subtitle}</p>
        )}
        {content.description && (
          <div className="prose prose-invert prose-sm max-w-none prose-p:text-slate-300">
            <ReactMarkdown>{content.description}</ReactMarkdown>
          </div>
        )}
      </div>

      {/* Hooks */}
      {content.hooks && content.hooks.length > 0 && (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">
            Key Hooks
          </h3>
          <ul className="space-y-3">
            {content.hooks.map((hook, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-400">
                    {i + 1}
                  </span>
                </div>
                <span className="text-slate-300 text-sm leading-relaxed">
                  {hook}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Secrets */}
      {content.secrets && content.secrets.length > 0 && (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">
            Secrets Revealed
          </h3>
          <div className="space-y-4">
            {content.secrets.map((secret, i) => (
              <div
                key={i}
                className="border-l-2 border-purple-500/30 pl-4"
              >
                <h4 className="text-sm font-semibold text-purple-300 mb-1">
                  {secret.title}
                </h4>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {secret.paragraph}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
